import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays, addHours, isBefore, startOfDay, parseISO, isAfter } from 'date-fns';
import axios from 'axios';
import { Calendar as CalendarIcon, Phone, User, Store, FileText, AlertCircle, CreditCard, Shield, Lock, Check, Mail, Tag, X, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Opening hours: Mon-Fri 8-4, Sat 8:30-4, Sun 9-4
const getTimeSlotsForDate = (date) => {
  if (!date) {
    // Default while no date is picked: union of all possible hours
    return ['08:00', '08:30', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];
  }
  const day = date.getDay(); // 0=Sun, 1=Mon … 6=Sat
  if (day === 0) {
    return ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];
  }
  if (day === 6) {
    return ['08:30', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];
  }
  return ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'];
};

// Google Icon Component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cartItems, cartTotal, sessionId, initialized, loading: cartLoading } = useCart();
  const { user, isAuthenticated, loginWithGoogle } = useAuth();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [checkoutMode, setCheckoutMode] = useState(isAuthenticated ? 'authenticated' : null); // null = choose, 'guest', 'authenticated'
  const [guestEmail, setGuestEmail] = useState('');
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Only pickup available (no delivery for now)
  const deliveryType = 'pickup';
  const [pickupDate, setPickupDate] = useState(null);
  const [pickupTime, setPickupTime] = useState('');
  const [contactName, setContactName] = useState(user?.name || '');
  const [contactPhone, setContactPhone] = useState(user?.phone || '');
  const [specialInstructions, setSpecialInstructions] = useState('');

  // Fetch settings and blackout dates
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, blackoutRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/blackout-dates`)
        ]);
        setSettings(settingsRes.data);
        setBlackoutDates(blackoutRes.data.map(d => d.date));
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchData();
  }, []);

  // Update checkout mode when auth changes
  useEffect(() => {
    if (isAuthenticated) {
      setCheckoutMode('authenticated');
    }
  }, [isAuthenticated]);

  // Get lead time from settings (default 2 days)
  const leadTimeHours = settings?.lead_time_hours || 48;
  const leadTimeDays = Math.ceil(leadTimeHours / 24);
  const minDate = addDays(new Date(), leadTimeDays);

  // Validate if selected date/time meets lead time requirement
  const validateLeadTime = () => {
    if (!pickupDate || !pickupTime) return null; // Not selected yet
    
    // Create datetime from selected date and time
    const [hours, minutes] = pickupTime.split(':').map(Number);
    const selectedDateTime = new Date(pickupDate);
    selectedDateTime.setHours(hours, minutes, 0, 0);
    
    // Calculate minimum allowed datetime (now + lead time hours)
    const minimumDateTime = addHours(new Date(), leadTimeHours);
    
    // Check if selected datetime is after minimum
    return isAfter(selectedDateTime, minimumDateTime);
  };

  const isLeadTimeValid = validateLeadTime();

  useEffect(() => {
    if (user) {
      setContactName(user.name || '');
      setContactPhone(user.phone || '');
    }
  }, [user]);

  useEffect(() => {
    // Only redirect after cart has been initialized and is empty
    if (initialized && !cartLoading && cartItems.length === 0) {
      navigate('/cart');
    }
  }, [cartItems, navigate, initialized, cartLoading]);

  const isDateDisabled = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Disable past dates, dates before lead time, and blackout dates
    return isBefore(date, startOfDay(minDate)) || blackoutDates.includes(dateStr);
  };

  // Pickup only - no delivery fee
  const finalTotal = Math.max(0, cartTotal - couponDiscount);

  // Validate coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    setCouponError('');
    
    try {
      const response = await axios.post(`${API}/coupons/validate`, null, {
        params: {
          code: couponCode.toUpperCase(),
          order_total: cartTotal,
          user_id: isAuthenticated ? user?.id : null,
          user_email: isAuthenticated ? user?.email : guestEmail
        }
      });
      
      setCouponDiscount(response.data.calculated_discount);
      setCouponApplied(response.data);
      toast.success(`Coupon applied! You save €${response.data.calculated_discount.toFixed(2)}`);
    } catch (error) {
      setCouponError(error.response?.data?.detail || 'Invalid coupon');
      setCouponDiscount(0);
      setCouponApplied(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponDiscount(0);
    setCouponApplied(null);
    setCouponError('');
  };

  const handleGoogleLogin = () => {
    // Store return URL as checkout page
    loginWithGoogle('/checkout');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!pickupDate || !pickupTime) {
      toast.error('Please select a date and time for collection');
      return;
    }

    if (isLeadTimeValid === false) {
      toast.error(`Please select a time at least ${leadTimeHours} hours from now`);
      return;
    }

    if (!contactName || !contactPhone) {
      toast.error('Please fill in your contact details');
      return;
    }

    if (checkoutMode === 'guest' && !guestEmail) {
      toast.error('Please enter your email for order confirmation');
      return;
    }

    // Stripe minimum charge is €0.50 for EUR
    if (finalTotal < 0.5) {
      toast.error(
        `Order total (€${finalTotal.toFixed(2)}) is below the minimum of €0.50 accepted by our payment processor. Please add more items to your cart.`
      );
      return;
    }

    setLoading(true);

    try {
      const orderDetails = {
        pickup_date: format(pickupDate, 'yyyy-MM-dd'),
        pickup_time: pickupTime,
        delivery_type: 'pickup',
        delivery_address: null,
        contact_phone: contactPhone,
        contact_name: contactName,
        contact_email: checkoutMode === 'guest' ? guestEmail : null,
        special_instructions: specialInstructions || null,
        coupon_code: couponApplied?.code || null,
        coupon_discount: couponDiscount || 0,
      };

      const response = await axios.post(
        `${API}/checkout/create-session`,
        {
          origin_url: window.location.origin,
          order_details: orderDetails,
        },
        {
          headers: isAuthenticated 
            ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
            : {},
          params: sessionId ? { session_id: sessionId } : {},
        }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a]">
            Checkout
          </h1>
          <p className="text-stone-500 mt-2">Complete your order details</p>
        </motion.div>

        {/* Guest/Login Selection - Only show if not authenticated and no mode selected */}
        <AnimatePresence mode="wait">
          {!isAuthenticated && checkoutMode === null && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mt-8"
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm max-w-2xl mx-auto">
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] text-center mb-2">
                  How would you like to checkout?
                </h2>
                <p className="text-stone-500 text-center mb-8">
                  Sign in for a faster checkout or continue as guest
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Google Sign In */}
                  <button
                    onClick={handleGoogleLogin}
                    className="flex items-center justify-center gap-3 p-4 rounded-xl border-2 border-stone-200 hover:border-[#8D6E63] hover:bg-[#8D6E63]/5 transition-all group"
                    data-testid="google-login-btn"
                  >
                    <GoogleIcon />
                    <div className="text-left">
                      <p className="font-semibold text-[#1a1a1a] group-hover:text-[#8D6E63]">
                        Continue with Google
                      </p>
                      <p className="text-xs text-stone-500">Fast & secure sign in</p>
                    </div>
                  </button>

                  {/* Guest Checkout */}
                  <button
                    onClick={() => setCheckoutMode('guest')}
                    className="flex items-center justify-center gap-3 p-4 rounded-xl border-2 border-stone-200 hover:border-[#8D6E63] hover:bg-[#8D6E63]/5 transition-all group"
                    data-testid="guest-checkout-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center group-hover:bg-[#8D6E63]/10">
                      <User className="w-5 h-5 text-stone-600 group-hover:text-[#8D6E63]" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-[#1a1a1a] group-hover:text-[#8D6E63]">
                        Checkout as Guest
                      </p>
                      <p className="text-xs text-stone-500">No account needed</p>
                    </div>
                  </button>
                </div>

                {/* Benefits of signing in */}
                <div className="mt-8 pt-6 border-t border-stone-100">
                  <p className="text-sm text-stone-500 text-center mb-4">
                    Benefits of creating an account:
                  </p>
                  <div className="flex flex-wrap justify-center gap-4 text-xs text-stone-600">
                    <div className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-500" />
                      <span>Track your orders</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-500" />
                      <span>Faster checkout</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-500" />
                      <span>Order history</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-500" />
                      <span>Exclusive offers</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Checkout Form - Show when mode is selected or user is authenticated */}
        {(checkoutMode === 'guest' || checkoutMode === 'authenticated') && (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
              {/* Form Section */}
              <div className="lg:col-span-2 space-y-8">
                
                {/* User Status Banner */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl p-4 shadow-sm"
                >
                  {isAuthenticated ? (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Check className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-[#1a1a1a]">Signed in as {user?.name || user?.email}</p>
                        <p className="text-xs text-stone-500">Your order will be saved to your account</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-stone-600" />
                        </div>
                        <div>
                          <p className="font-medium text-[#1a1a1a]">Checking out as Guest</p>
                          <p className="text-xs text-stone-500">We'll send your receipt to your email</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setCheckoutMode(null)}
                        className="text-[#8D6E63] hover:text-[#8D6E63]/80"
                      >
                        Sign in instead
                      </Button>
                    </div>
                  )}
                </motion.div>

                {/* Guest Email - Only for guests */}
                {checkoutMode === 'guest' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl p-6 shadow-sm"
                  >
                    <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-[#8D6E63]" />
                      Email for Order Confirmation
                    </h2>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="guest-email-input"
                      />
                    </div>
                    <p className="text-xs text-stone-500 mt-2">
                      We'll send your order confirmation and updates to this email
                    </p>
                  </motion.div>
                )}

                {/* Collection Location */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-sm"
                >
                  <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a] mb-6 flex items-center gap-2">
                    <Store className="w-5 h-5 text-[#8D6E63]" />
                    Collection Location
                  </h2>

                  <div className="flex items-center gap-4 p-4 rounded-xl border-2 border-[#8D6E63] bg-[#8D6E63]/5">
                    <Store className="w-6 h-6 text-[#8D6E63]" />
                    <div className="flex-1">
                      <p className="font-semibold text-[#1a1a1a]">Mad Brothers Bakery</p>
                      <p className="text-sm text-stone-600">{settings?.store_address || '17 Nephin Road, Dublin 7, D07 N8K7'}</p>
                      <p className="text-xs text-stone-500 mt-1">Mon-Sat: 9AM - 6PM</p>
                    </div>
                    <span className="text-green-600 font-semibold bg-green-50 px-3 py-1 rounded-full text-sm">
                      Free Collection
                    </span>
                  </div>
                </motion.div>

              {/* Date & Time Selection */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-6 shadow-sm"
              >
                <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a] mb-6 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-[#8D6E63]" />
                  Collection Date & Time
                </h2>

                {/* Lead Time Validation Badge */}
                {isLeadTimeValid === null ? (
                  // Not yet selected - show warning
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-6">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <p className="text-sm text-amber-700 font-medium">
                      {leadTimeHours}-hour lead time required for all orders
                    </p>
                  </div>
                ) : isLeadTimeValid ? (
                  // Valid - show green
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-6"
                  >
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-700 font-medium">
                      ✓ Collection time meets the {leadTimeHours}-hour lead time requirement
                    </p>
                  </motion.div>
                ) : (
                  // Invalid - show red
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-6"
                  >
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-red-700 font-medium">
                        ✗ Selected time does not meet the {leadTimeHours}-hour lead time requirement
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Please select a date and time at least {leadTimeHours} hours from now
                      </p>
                    </div>
                  </motion.div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {/* Date Picker */}
                  <div>
                    <Label className="text-sm font-medium">Select Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full mt-2 justify-start text-left font-normal ${
                            isLeadTimeValid === false ? 'border-red-300 bg-red-50' : 
                            isLeadTimeValid === true ? 'border-green-300 bg-green-50' : ''
                          }`}
                          data-testid="date-picker-trigger"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {pickupDate ? format(pickupDate, 'PPP') : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={pickupDate}
                          onSelect={(d) => {
                            setPickupDate(d);
                            // Clear time if it's no longer valid for the new day
                            if (d && pickupTime && !getTimeSlotsForDate(d).includes(pickupTime)) {
                              setPickupTime('');
                            }
                          }}
                          disabled={isDateDisabled}
                          initialFocus
                          data-testid="date-calendar"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Time Selector */}
                  <div>
                    <Label className="text-sm font-medium">Select Time</Label>
                    <Select value={pickupTime} onValueChange={setPickupTime}>
                      <SelectTrigger 
                        className={`mt-2 ${
                          isLeadTimeValid === false ? 'border-red-300 bg-red-50' : 
                          isLeadTimeValid === true ? 'border-green-300 bg-green-50' : ''
                        }`} 
                        data-testid="time-select"
                      >
                        <SelectValue placeholder="Select time slot" />
                      </SelectTrigger>
                      <SelectContent>
                        {getTimeSlotsForDate(pickupDate).map((time) => (
                          <SelectItem key={time} value={time} data-testid={`time-${time}`}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </motion.div>

              {/* Contact Details */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl p-6 shadow-sm"
              >
                <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a] mb-6 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#8D6E63]" />
                  Contact Details
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium">Name</Label>
                    <div className="relative mt-2">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        id="name"
                        placeholder="Your name"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium">Phone Number</Label>
                    <div className="relative mt-2">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                      <Input
                        id="phone"
                        placeholder="Your phone number"
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <Label htmlFor="instructions" className="text-sm font-medium">
                    Special Instructions (Optional)
                  </Label>
                  <div className="relative mt-2">
                    <FileText className="absolute left-3 top-3 w-5 h-5 text-stone-400" />
                    <Textarea
                      id="instructions"
                      placeholder="Any special requests or instructions..."
                      value={specialInstructions}
                      onChange={(e) => setSpecialInstructions(e.target.value)}
                      className="pl-10 min-h-[100px]"
                      data-testid="special-instructions-input"
                    />
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
                <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#1a1a1a] mb-6">
                  Order Summary
                </h2>

                {/* Items */}
                <div className="space-y-4 max-h-[300px] overflow-y-auto">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex gap-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={getImageUrl(item.image)} alt={item.product_name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1a1a1a] text-sm truncate">{item.product_name}</p>
                        {item.size_name && <p className="text-xs text-stone-500">{item.size_name}</p>}
                        {item.flavor_name && <p className="text-xs text-stone-500">{item.flavor_name}</p>}
                        <p className="text-xs text-stone-500">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-medium text-[#1a1a1a]">€{item.total_price.toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-stone-200 mt-6 pt-4 space-y-3">
                  <div className="flex justify-between text-stone-600">
                    <span>Subtotal</span>
                    <span>€{cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-stone-600">
                    <span>Collection</span>
                    <span className="text-green-600">FREE</span>
                  </div>
                  
                  {/* Coupon Discount */}
                  {couponApplied && (
                    <div className="flex justify-between text-green-600">
                      <span className="flex items-center gap-1">
                        <Tag className="w-4 h-4" />
                        {couponApplied.code}
                        <button onClick={handleRemoveCoupon} className="ml-1 text-stone-400 hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                      <span>-€{couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-lg font-bold text-[#1a1a1a] pt-3 border-t border-stone-200">
                    <span>Total</span>
                    <span data-testid="checkout-total">€{finalTotal.toFixed(2)}</span>
                  </div>
                </div>

                {/* Coupon Code Input */}
                {!couponApplied && (
                  <div className="mt-4 pt-4 border-t border-stone-100">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                        <Input
                          type="text"
                          placeholder="Discount code"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          className="pl-10 font-mono text-sm"
                          data-testid="coupon-input"
                        />
                      </div>
                      <Button 
                        type="button"
                        variant="outline" 
                        onClick={handleApplyCoupon}
                        disabled={validatingCoupon || !couponCode.trim()}
                        className="px-4"
                      >
                        {validatingCoupon ? '...' : 'Apply'}
                      </Button>
                    </div>
                    {couponError && (
                      <p className="text-red-500 text-xs mt-2">{couponError}</p>
                    )}
                    {isAuthenticated && (
                      <p className="text-xs text-stone-500 mt-2">
                        Use <span className="font-mono text-[#8D6E63]">MADBRO10</span> for 10% off your first order!
                      </p>
                    )}
                    {!isAuthenticated && checkoutMode === 'guest' && (
                      <p className="text-xs text-stone-500 mt-2">
                        <button 
                          type="button"
                          onClick={() => setCheckoutMode(null)} 
                          className="text-[#8D6E63] underline"
                        >
                          Sign in
                        </button> to use first-purchase discount codes
                      </p>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  className={`w-full mt-6 rounded-full h-12 text-base btn-shine flex items-center justify-center gap-2 ${
                    isLeadTimeValid === false 
                      ? 'bg-stone-300 cursor-not-allowed' 
                      : 'bg-[#1a1a1a] hover:bg-[#1a1a1a]/90'
                  }`}
                  disabled={loading || isLeadTimeValid === false}
                  data-testid="pay-btn"
                >
                  <CreditCard className="w-5 h-5" />
                  {loading ? 'Processing...' : isLeadTimeValid === false ? 'Select Valid Collection Time' : `Pay €${finalTotal.toFixed(2)}`}
                </Button>

                {/* Payment Security Badges */}
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-center gap-2 text-stone-500">
                    <Lock className="w-4 h-4" />
                    <span className="text-xs">Secure SSL Encrypted Payment</span>
                  </div>
                  
                  <div className="flex items-center justify-center gap-3 py-3 border-t border-stone-100">
                    {/* Payment method icons */}
                    <div className="flex items-center gap-2">
                      <svg className="h-6" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.3 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3Z" fill="#000" opacity=".07"/>
                        <path d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32" fill="#fff"/>
                        <path d="M28.3 10.1H28c-.4 1-.7 1.5-1 3h1.9c-.3-1.5-.3-2.2-.6-3Zm2.9 5.9h-1.7c-.1 0-.1 0-.2-.1l-.2-.9-.1-.2h-2.4c-.1 0-.2 0-.2.2l-.3.9c0 .1-.1.1-.1.1h-2.1l.2-.5L27 8.7c0-.5.3-.7.8-.7h1.5c.1 0 .2 0 .2.2l1.4 6.5c.1.4.2.7.2 1.1.1.1.1.1.1.2Zm-13.4-.3.4-1.8c.1 0 .2.1.2.1.7.3 1.4.5 2.1.4.2 0 .5-.1.7-.2.5-.2.5-.7.1-1.1-.2-.2-.5-.3-.8-.5-.4-.2-.8-.4-1.1-.7-1.2-1-.8-2.4-.1-3.1.6-.4.9-.8 1.7-.8 1.2 0 2.5 0 3.1.2h.1c-.1.6-.2 1.1-.4 1.7-.5-.2-1-.4-1.5-.4-.3 0-.6 0-.9.1-.2 0-.3.1-.4.2-.2.2-.2.5 0 .7l.5.4c.4.2.8.4 1.1.6.5.3 1 .8 1.1 1.4.2.9-.1 1.7-.9 2.3-.5.4-.7.6-1.4.6-1.4 0-2.5.1-3.4-.2-.1.2-.1.2-.2.1Zm-3.5.3c.1-.7.1-.7.2-1 .5-2.2 1-4.5 1.4-6.7.1-.2.1-.3.3-.3H18c-.2 1.2-.4 2.1-.7 3.2-.3 1.5-.6 3-1 4.5 0 .2-.1.2-.3.2h-1.1c-.1 0-.2 0-.2-.2v-.7Zm-6.9 0c.2-1.1.3-1.9.5-2.7 0-.1 0-.1.1-.2h1.8c.1 0 .2 0 .2.2v.5c0 .1.1.2.2.1.4-.3.8-.5 1.3-.6.6-.1 1.2.1 1.4.7.2.6.3 1.2.3 1.8 0 .8-.1 1.6-.4 2.3-.3.6-.7 1.1-1.3 1.4-.6.3-1.2.4-1.8.3-.4 0-.8-.1-1.1-.4h-.1c-.1.1-.1.3-.1.5v1c0 .1 0 .2-.2.2H8c-.1.1-.2 0-.2-.2-.1-.6 0-1.3 0-2V16Zm1.5-2c-.1.7-.1 1.3 0 2 .1.4.2.7.5.8.2.1.5.1.7 0s.4-.2.5-.4c.2-.3.3-.7.4-1.1.1-.5.1-1 0-1.5-.1-.4-.3-.7-.6-.8-.2-.1-.5-.1-.8 0-.3.1-.5.3-.6.6v.4h-.1Z" fill="#142688"/>
                      </svg>
                      <svg className="h-6" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.3 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3Z" fill="#000" opacity=".07"/>
                        <path d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32" fill="#fff"/>
                        <circle cx="15" cy="12" r="7" fill="#eb001b"/>
                        <circle cx="23" cy="12" r="7" fill="#f79e1b"/>
                        <path d="M22 12c0-2.4-1.2-4.5-3-5.7-1.8 1.3-3 3.4-3 5.7 0 2.3 1.2 4.5 3 5.7 1.8-1.2 3-3.3 3-5.7Z" fill="#ff5f00"/>
                      </svg>
                      <svg className="h-6" viewBox="0 0 38 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.3 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.3-3-3-3Z" fill="#000" opacity=".07"/>
                        <path d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32" fill="#fff"/>
                        <path d="M8.971 10.268l.774-1.876 1.876 1.876h-2.65Zm9.888 4.276L13.987 8.63h-1.609L8.87 17.418h1.712l.679-1.712h3.322l.679 1.712h1.712l-.116-.28v.001ZM8.064 8.63h1.606L12.4 14.3l1.195-5.67h1.608L12.89 17.42h-1.609L8.065 8.63h-.001Zm12.082 5.822h-3.438v3.014h3.438v-1.109h-2.024V14.45h1.024v-.998h-1.024v-.998h2.024v-1.003Zm-.986-2.984L16.6 17.418h1.69l2.56-5.951h-1.69Zm3.893 0L20.73 17.02h1.69l2.323-5.551h-1.69Z" fill="#1a1f71"/>
                      </svg>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-stone-400 text-xs">
                    <Shield className="w-3 h-3" />
                    <span>Powered by Stripe • Apple Pay & Google Pay accepted</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
